import { Injectable } from '@angular/core';
import { GRAPHIC_OBJECT_FIELDS } from 'src/app/core/enums/graphic-object-fields';
import { CameraInstructions } from 'src/app/core/models/3D/utils/camera-instructions';
import { Floating } from 'src/app/core/models/floating.model';
import { PMath } from 'src/app/shared/utils/recurrent-math/project-math';
import {
  Event,
  Group,
  Intersection,
  Object3D,
  Raycaster,
  Renderer,
  Scene,
  Vector2,
  Vector3,
} from 'three';
import { MapsService } from '../../simulation/objects/maps/maps.service';
import { CameraService } from '../camera/camera.service';
import { ControlsService } from '../controls/controls.service';
import { StatesService } from '../states/states.service';
import { ThreeJsHelperService } from '../three-d-helper/three_d_helper.service';

@Injectable({
  providedIn: 'root',
})
export class CentralizerService {
  private readonly raycaster: Raycaster;
  private readonly mouse: Vector2;

  constructor(
    private threeJsHelper: ThreeJsHelperService,
    private cameraService: CameraService,
    private controlsService: ControlsService,
    private mapService: MapsService,
    private statesService: StatesService,
  ) {
    this.raycaster = new Raycaster();
    this.raycaster.far = 100000;

    this.mouse = new Vector2();
  }

  public centralizeSimulationObjectByCredentials(objectTitle: string, objectId: number): void {
    if (!objectTitle || !objectId) {
      return;
    }

    const object3d = this.scene.getObjectByName(`[${objectTitle}].${objectId}`);
    if (!object3d) {
      return;
    }

    const selectedObjectResponse = new SelectedObjectResponse(
      object3d,
      this.mapService.getTypeById(objectId),
    );

    this.triggerCameraMovement(selectedObjectResponse);
  }

  public centralizeObjectsOnMouseEvent(event: MouseEvent): void {
    event.preventDefault();
    const selectedObjectResponse: SelectedObjectResponse = this.getSelectedObject(event);
    if (selectedObjectResponse === null || selectedObjectResponse.object === null) {
      return;
    }

    this.triggerCameraMovement(selectedObjectResponse);
  }

  private triggerCameraMovement(selectedObject: SelectedObjectResponse) {
    const cameraInstructions: CameraInstructions =
      this.getCameraInstructionsByObject(selectedObject);
    this.statesService.focus.next(cameraInstructions);
  }

  private getSelectedObject(event: MouseEvent): any {
    const interceptedObjects = this.findIntersectedObjects(event);
    if (interceptedObjects.length === 0) {
      return null;
    }

    return this.getNearestSelectableObject(interceptedObjects);
  }

  private findIntersectedObjects(event: MouseEvent): Intersection[] {
    const deltaX = window.innerWidth - this.renderer.domElement.clientWidth;
    const deltaY = window.innerHeight - this.renderer.domElement.clientHeight;

    this.mouse.x = ((event.clientX - deltaX) / this.renderer.domElement.clientWidth) * 2 - 1;
    this.mouse.y = -((event.clientY - deltaY) / this.renderer.domElement.clientHeight) * 2 + 1;

    this.cameraService.camera.updateMatrixWorld();
    this.raycaster.setFromCamera(this.mouse, this.cameraService.camera);
    return this.raycaster.intersectObjects(this.scene.children, true);
  }

  private getNearestSelectableObject(interceptedObjects: Intersection[]): SelectedObjectResponse {
    let index = 0;
    while (index < interceptedObjects.length) {
      const nearestObject: Intersection = interceptedObjects[index];
      const objectType: string = this.threeJsHelper.getObjectType(nearestObject.object);

      if (objectType !== null) {
        return new SelectedObjectResponse(nearestObject.object, objectType);
      }

      index++;
    }
    return null;
  }

  private getCameraInstructionsByObject(
    selectedObjectResponse: SelectedObjectResponse,
  ): CameraInstructions {
    switch (selectedObjectResponse.objectType) {
      case GRAPHIC_OBJECT_FIELDS.LINE:
        return this.lineCameraPosition(selectedObjectResponse.object);

      case GRAPHIC_OBJECT_FIELDS.FLOATING:
        return this.floatingCameraPosition(selectedObjectResponse.object);

      default:
        return this.genericCameraPosition(selectedObjectResponse.object);
    }
  }

  private genericCameraPosition(object: Object3D<Event> | Group): CameraInstructions {
    const cameraOffset = PMath.getVectorProjectionInXY(
      object.position,
      this.cameraService.camera.position,
    );

    cameraOffset.z = -0.5;
    cameraOffset.multiplyScalar(-500);

    const cameraPosition = object.position.clone().add(cameraOffset);

    return new CameraInstructions(object.position, cameraPosition);
  }

  private lineCameraPosition(line: Object3D<Event>): CameraInstructions {
    // ToDo: criar uma versão completa das instruções
    return new CameraInstructions(new Vector3(0, 0, 0), new Vector3(500, 500, 500));
  }

  private floatingCameraPosition(floatingChild3d: Object3D<Event>): CameraInstructions {
    const floating3d = this.threeJsHelper.getParent(floatingChild3d);
    const simuFloating: Floating =
      this.threeJsHelper.getSimulationObjectByThreeJsObject(floatingChild3d);
    const rotation = PMath.azimuthDegToRotationRad(simuFloating.azimuth);

    let x = simuFloating.dimensions[0];
    let y = simuFloating.dimensions[1];
    let z = simuFloating.dimensions[2];

    let max;
    let zFactor;
    const isZTheMax = z > x && z > y;

    if (isZTheMax) {
      max = z;
      zFactor = 2.1;
    } else {
      max = x < y ? y : x;
      zFactor = 2.5;
    }

    let sizeFactor = Math.pow(2, -max / 60) + 1;
    sizeFactor *= sizeFactor;

    const { xRotation, yRotation } = this.getMatrixRotationRad(rotation);

    x = 1.5 * sizeFactor * max * xRotation;
    y = 1.5 * sizeFactor * max * yRotation;
    z = zFactor * z;

    const cameraPosition = new Vector3(x, y, z).add(floating3d.position);
    const cameraTarget = new Vector3().copy(floating3d.position);

    if (isZTheMax) {
      cameraTarget.sub(new Vector3(xRotation, yRotation, 0).multiplyScalar(0.2 * max));
    }

    return new CameraInstructions(cameraTarget, cameraPosition);
  }

  private getMatrixRotationRad(angle: number): {
    xRotation: number;
    yRotation: number;
  } {
    const xRotation = Math.cos(angle) - Math.sin(angle);
    const yRotation = Math.sin(angle) + Math.cos(angle);

    return { xRotation, yRotation };
  }

  private get renderer(): Renderer {
    return this.statesService.renderer.getValue();
  }
  private get scene(): Scene {
    return this.statesService.scene.getValue();
  }
}

class SelectedObjectResponse {
  object: Object3D<Event>;
  objectType: string;

  constructor(object: Object3D<Event>, objectType: string) {
    this.object = object;
    this.objectType = objectType;
  }
}
